import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { ReactLenis } from '@studio-freight/react-lenis';
import LoadingScreen from './components/LoadingScreen';
import './App.css';
import Header from './components/Header';
import MobileNav from './components/MobileNav';
import { NeuralNetworkBackground } from './components/NeuralNetworkBackground';
import { trackVisit } from './utils/analytics';
import FloatingCalculator from './components/FloatingCalculator';
import FloatingSpeedTest from './components/FloatingSpeedTest';
import ScrollToTop from './components/ScrollToTop';
import ErrorBoundary from './components/ErrorBoundary';

const Team = lazy(() => import('./pages/Team'));
const About = lazy(() => import('./components/About'));
const Home = lazy(() => import('./Home/Home'));
const ServicesPage = lazy(() => import('./pages/ServicesPage'));
const Footer = lazy(() => import('./components/Footer'));
const AdminLogin = lazy(() => import('./pages/admin/Login'));
const Dashboard = lazy(() => import('./pages/admin/Dashboard'));
const Projects = lazy(() => import('./pages/admin/Projects'));
const Services = lazy(() => import('./pages/admin/Services'));
const Articles = lazy(() => import('./pages/admin/Articles'));
const Comments = lazy(() => import('./pages/admin/Comments'));
const TestimonialsAdmin = lazy(() => import('./pages/admin/Testimonials'));
const Messages = lazy(() => import('./pages/admin/Messages'));
const Subscribers = lazy(() => import('./pages/admin/Subscribers'));
const Settings = lazy(() => import('./pages/admin/Settings'));
const PackagesPage = lazy(() => import('./pages/PackagesPage'));
const ProjectDetails = lazy(() => import('./components/ProjectDetails'));
const Contact = lazy(() => import('./pages/Contact'));
const BlogDetail = lazy(() => import('./pages/BlogDetail'));
const ArticlesPage = lazy(() => import('./pages/ArticlesPage'));
const NotFound = lazy(() => import('./pages/NotFound'));
const StatusPage = lazy(() => import('./pages/StatusPage'));
const SpeedTest = lazy(() => import('./pages/SpeedTest'));

// Layout wrapper component
const ScrollToTopHelper = () => {
  const { pathname } = useLocation();
  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

const Layout = ({ children }) => {
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith('/admin');

  const isStatusOrNotFound = React.useMemo(() => {
    const pathname = location.pathname;
    const statusRoutes = ['/401', '/402', '/403', '/410', '/500', '/503', '/offline'];
    if (statusRoutes.includes(pathname) || pathname.startsWith('/status/')) {
      return true;
    }
    const standardRoutes = ['/', '/services', '/contact', '/about', '/team', '/packages', '/speed-test', '/blog', '/articles'];
    if (standardRoutes.includes(pathname)) {
      return false;
    }
    if (pathname.startsWith('/portfolio/') || pathname.startsWith('/blog/')) {
      return false;
    }
    return true;
  }, [location.pathname]);

  React.useEffect(() => {
    if (isAdminRoute) {
      import('./firebase/config').then(({ enableNetwork }) => {
        enableNetwork();
      });
    }
  }, [location.pathname, isAdminRoute]);

  const showHeaderFooter = !isAdminRoute && !isStatusOrNotFound;

  const layoutContent = (
    <div className="relative z-10 flex flex-col min-h-screen">
      {showHeaderFooter && <Header />}
      <main className="flex-grow pb-24 md:pb-0">
        {children}
      </main>
      {showHeaderFooter && (
        <>
          <FloatingCalculator />
          <FloatingSpeedTest />
          <ScrollToTop />
          <Footer />
          <MobileNav />
        </>
      )}
    </div>
  );

  return (
    <div className="flex flex-col min-h-screen bg-primary relative overflow-hidden">
      {/* Global Background Grid and Glowing Orbs */}
      {!isAdminRoute && (
        <div className="fixed inset-0 z-0 pointer-events-none overflow-hidden">
          <div className="absolute inset-0 bg-primary" />
          {/* Grid pattern */}
          <div className="absolute inset-0" style={{
            backgroundImage: `
              linear-gradient(rgba(255, 255, 255, 0.03) 1px, transparent 1px),
              linear-gradient(90deg, rgba(255, 255, 255, 0.03) 1px, transparent 1px)
            `,
            backgroundSize: '60px 60px',
            backgroundPosition: 'center center',
          }} />
          {/* Glowing orbs */}
          <div className="glowing-orb orb-1" />
          <div className="glowing-orb orb-2" />
          <div className="glowing-orb orb-3" />
          {/* Animated neural network grid */}
          <NeuralNetworkBackground />
        </div>
      )}

      {!isAdminRoute ? (
        <ReactLenis root>
          {layoutContent}
        </ReactLenis>
      ) : layoutContent}
    </div>
  );
};

const App = () => {
  React.useEffect(() => {
    trackVisit();
  }, []);

  return (
    <HelmetProvider>
      <BrowserRouter>
        <ScrollToTopHelper />
        <Suspense fallback={<LoadingScreen />}>
          <ErrorBoundary>
            <Layout>
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/services" element={<ServicesPage />} />
                <Route path="/portfolio/:projectId" element={<ProjectDetails />} />
                <Route path="/admin" element={<AdminLogin />} />
                <Route path="/admin/dashboard" element={<Dashboard />} />
                <Route path="/admin/projects" element={<Projects />} />
                <Route path="/admin/services" element={<Services />} />
                <Route path="/admin/articles" element={<Articles />} />
                <Route path="/admin/comments" element={<Comments />} />
                <Route path="/admin/testimonials" element={<TestimonialsAdmin />} />
                <Route path="/admin/messages" element={<Messages />} />
                <Route path="/admin/subscribers" element={<Subscribers />} />
                <Route path="/admin/settings" element={<Settings />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/about" element={<About />} />
                <Route path="/team" element={<Team />} />
                <Route path="/packages" element={<PackagesPage />} />
                <Route path="/speed-test" element={<SpeedTest />} />
                <Route path="/blog" element={<ArticlesPage />} />
                <Route path="/articles" element={<ArticlesPage />} />
                <Route path="/blog/:id" element={<BlogDetail />} />
                <Route path="/410" element={<StatusPage code="410" />} />
                <Route path="/402" element={<StatusPage code="402" />} />
                <Route path="/403" element={<StatusPage code="403" />} />
                <Route path="/401" element={<StatusPage code="401" />} />
                <Route path="/500" element={<StatusPage code="500" />} />
                <Route path="/503" element={<StatusPage code="503" />} />
                <Route path="/offline" element={<StatusPage code="offline" />} />
                <Route path="/status/:code" element={<StatusPage />} />
                <Route path="*" element={<NotFound />} />

              </Routes>
            </Layout>
          </ErrorBoundary>
        </Suspense>
      </BrowserRouter>
    </HelmetProvider>
  );
};

export default App;
